import React from 'react';
import {
  Box, HStack, Text, useColorModeValue,
} from '@chakra-ui/react';
import { FiCheckCircle, FiLock, FiSlash } from 'react-icons/fi';

export type StampTone = 'positive' | 'neutral' | 'negative';

interface DocumentStatusStampProps {
  label: string;
  detail?: string;
  tone?: StampTone;
}

// item 441 — the quiet terminal-state ribbon on document edit forms: once a doc is
// approved / finalized / paid / closed / void it can't be edited, so say WHAT it is
// and what to do instead. Deliberately flat (no glass) so both skins render it clean;
// the PDF twin is the diagonal watermark (DocumentPdfDocument.Watermark).
const DocumentStatusStamp: React.FC<DocumentStatusStampProps> = ({ label, detail, tone = 'neutral' }) => {
  // all palettes resolve every render (hook-count stability); tone just picks one
  const positive = {
    bg: useColorModeValue('green.50', 'rgba(72, 187, 120, 0.12)'),
    border: useColorModeValue('green.300', 'green.700'),
    fg: useColorModeValue('green.700', 'green.200'),
    icon: FiCheckCircle,
  };
  const neutral = {
    bg: useColorModeValue('gray.50', 'whiteAlpha.100'),
    border: useColorModeValue('gray.300', 'whiteAlpha.300'),
    fg: useColorModeValue('gray.600', 'whiteAlpha.800'),
    icon: FiLock,
  };
  const negative = {
    bg: useColorModeValue('red.50', 'rgba(229, 62, 62, 0.12)'),
    border: useColorModeValue('red.300', 'red.700'),
    fg: useColorModeValue('red.600', 'red.300'),
    icon: FiSlash,
  };
  const p = { positive, neutral, negative }[tone];
  const detailColor = useColorModeValue('gray.600', 'whiteAlpha.700');

  return (
    <HStack
      spacing={3}
      mb={4}
      px={4}
      py={2.5}
      borderWidth="1px"
      borderRadius="md"
      borderColor={p.border}
      bg={p.bg}
      align="center"
    >
      <Box as={p.icon} color={p.fg} fontSize="18px" flexShrink={0} />
      <Text fontSize="sm" fontWeight="bold" letterSpacing="0.08em" color={p.fg} flexShrink={0}>
        {label}
      </Text>
      {detail && (
        <Text fontSize="sm" color={detailColor} noOfLines={2}>
          {detail}
        </Text>
      )}
    </HStack>
  );
};

DocumentStatusStamp.defaultProps = { detail: undefined, tone: 'neutral' };

export default DocumentStatusStamp;
